import React, { useEffect, useMemo, useState } from "react";
import AdminPageHeader from "../../components/AdminPageHeader";
import { fixDuplicateEmployees } from "../../utils/fixDuplicateEmployees";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import {
  db,
  auth,
  callSetUserRole,
  callNotifyApplicantsBulk,
  callBootstrapSuperUser,
  callSendCustomEmailBulk,
} from "../../lib/firebase";
import { readAllDays } from "../../utils/scheduleStore";
import { getSlotHours } from "../../utils/scheduleHours";
import useUserRole, { isSuperRole } from "../../hooks/useUserRole";
import useRolePermissions from "../../hooks/useRolePermissions";
import ProfileModal from "./ProfileModal";
import "./EmployeeList.css";

const BOOTSTRAP_EMAIL = "joshrecker@gmail.com";

const DISPLAY_POSITION_OPTIONS = [
  "Tech",
  "EMR",
  "EMT",
  "AEMT",
  "Paramedic",
  "RN",
  "PA",
  "APRN",
  "Resident",
  "Physician",
  "Student",
  "Volunteer",
  "Dispatcher",
  "Supervisor",
  "Manager",
  "Director",
];

const BASE_POSITION_OPTIONS = [
  "Tech",
  "EMR",
  "EMT",
  "AEMT",
  "Paramedic",
  "RN",
  "PA",
  "APRN",
  "Resident",
  "Physician",
  "Student",
  "Volunteer",
  "Dispatcher",
  "Manager",
  "Director",
];

const RATE_LABEL_OPTIONS = ["", "THONE Emp"];

function prettyErr(e, fallback = "Request failed.") {
  return (
    e?.message ||
    e?.details?.message ||
    (typeof e?.details === "string" ? e.details : "") ||
    fallback
  );
}

const normalizeEmail = (v) => String(v || "").trim().toLowerCase();

function getEmployeeName(emp) {
  return emp?.name || emp?.displayName || "(no name)";
}

function getEmployeeEmail(emp) {
  return emp?.email || emp?.emailKey || emp?.authEmail || emp?.userEmail || "";
}

function getEmployeePosition(emp) {
  return emp?.position || emp?.title || emp?.role || emp?.providerLevel || "";
}

function getEmployeeBasePosition(emp) {
  return (
    emp?.basePosition ||
    emp?.providerLevel ||
    emp?.licenseLevel ||
    emp?.classification ||
    emp?.position ||
    ""
  );
}

function getEmployeeRateLabel(emp) {
  return String(emp?.payTitle || "").trim();
}

function isNewStaff(person) {
  const workedBefore = String(person?.workedBefore || "").trim().toLowerCase();
  return workedBefore === "" || workedBefore === "no";
}

function getEmployeeRowClass(emp) {
  const isNew = isNewStaff(emp);
  const isActive = emp?.active !== false;

  if (emp?.isDuplicate) return "employee-duplicate-row";
  if (isNew && !isActive) return "employee-new-inactive-row";
  if (isNew && isActive) return "employee-new-active-row";
  if (!isNew && !isActive) return "employee-inactive-row";
  return "";
}

function timeStringToHours(start, end) {
  const [sh, sm] = String(start || "").split(":").map(Number);
  const [eh, em] = String(end || "").split(":").map(Number);

  if (
    !Number.isFinite(sh) ||
    !Number.isFinite(sm) ||
    !Number.isFinite(eh) ||
    !Number.isFinite(em)
  ) {
    return 0;
  }

  const mins = eh * 60 + em - (sh * 60 + sm);
  return mins > 0 ? mins / 60 : 0;
}

function getDefaultBasePosition(displayPosition, currentBasePosition = "") {
  const current = String(currentBasePosition || "").trim();

  if (displayPosition === "Supervisor") {
    if (["EMT", "AEMT", "Paramedic"].includes(current)) return current;
    return "Paramedic";
  }

  if (displayPosition === "Student") return "Student";
  if (displayPosition === "Volunteer") return "Volunteer";
  if (displayPosition === "Resident") return "Resident";

  return displayPosition || current || "";
}

function getDefaultRateLabel(displayPosition, currentRateLabel = "") {
  if (String(currentRateLabel || "").trim() === "THONE Emp") return "THONE Emp";
  return "";
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (value && typeof value !== "string") return value;
  }
  return "";
}

function hasValue(value) {
  if (typeof value === "string") return value.trim().length > 0;
  return !!value;
}

function getPhoneText(emp) {
  return firstNonEmpty(
    emp?.phone,
    emp?.cellPhone,
    emp?.mobilePhone,
    emp?.businessPhone
  );
}

function getEmergencyContactText(emp) {
  return firstNonEmpty(emp?.emergencyContact, emp?.emergencyContactName);
}

function getEmergencyPhoneText(emp) {
  return firstNonEmpty(emp?.emergencyPhone, emp?.emergencyContactPhone);
}

function getAddressText(emp) {
  const direct = firstNonEmpty(
    emp?.address,
    emp?.homeAddress,
    emp?.mailingAddress,
    emp?.streetAddress
  );
  if (direct) return direct;

  const line1 = firstNonEmpty(emp?.addressLine1, emp?.street1, emp?.street);
  const line2 = firstNonEmpty(emp?.addressLine2, emp?.street2, emp?.apt);
  const city = firstNonEmpty(emp?.city);
  const state = firstNonEmpty(emp?.state);
  const zip = firstNonEmpty(emp?.zip, emp?.zipCode, emp?.postalCode);

  const parts = [
    [line1, line2].filter(Boolean).join(" ").trim(),
    [city, state, zip].filter(Boolean).join(", ").replace(", ,", ","),
  ].filter(Boolean);

  return parts.join(" | ") || "—";
}

function getProfilePhotoUrl(emp) {
  return firstNonEmpty(
    emp?.profilePhotoURL,
    emp?.profilePhotoUrl,
    emp?.photoURL,
    emp?.photoUrl,
    emp?.avatarURL,
    emp?.avatarUrl,
    emp?.headshotURL,
    emp?.headshotUrl
  );
}

function getSignatureUrl(emp) {
  return firstNonEmpty(
    emp?.signatureURL,
    emp?.signatureUrl,
    emp?.signatureImageURL,
    emp?.signatureImageUrl,
    emp?.signaturePngURL,
    emp?.signaturePngUrl
  );
}

function normalizeCredentialItems(emp) {
  const rawGroups = [
    emp?.credentials,
    emp?.credentialFiles,
    emp?.licenseFiles,
    emp?.licenses,
    emp?.certifications,
    emp?.uploadedCredentials,
  ].filter(Boolean);

  const out = [];

  rawGroups.forEach((group) => {
    if (Array.isArray(group)) {
      group.forEach((item, index) => {
        if (!item) return;

        if (typeof item === "string") {
          out.push({
            id: `cred-${index}-${item}`,
            name: "Credential File",
            url: item,
            storagePath: "",
            gsUrl: "",
            expires: "",
            status: "",
            uploadedAt: "",
          });
          return;
        }

        if (typeof item === "object") {
          out.push({
            id:
              item.id ||
              item.name ||
              item.fileName ||
              item.filename ||
              item.label ||
              `cred-${index}`,
            name:
              item.name ||
              item.label ||
              item.title ||
              item.fileName ||
              item.filename ||
              item.originalName ||
              item.type ||
              "Credential",
            url:
              item.url ||
              item.downloadURL ||
              item.downloadUrl ||
              item.fileUrl ||
              item.fileURL ||
              item.storageUrl ||
              item.storageURL ||
              item.publicUrl ||
              item.publicURL ||
              item.href ||
              "",
            storagePath:
              item.storagePath ||
              item.fullPath ||
              item.path ||
              item.filePath ||
              item.storageRef ||
              "",
            gsUrl: item.gsUrl || item.gsURL || "",
            expires:
              item.expirationDate ||
              item.expires ||
              item.expiry ||
              item.expDate ||
              "",
            status: item.status || "",
            uploadedAt:
              item.uploadedAt ||
              item.createdAt ||
              item.timestamp ||
              item.dateUploaded ||
              "",
          });
        }
      });
      return;
    }

    if (group && typeof group === "object") {
      Object.entries(group).forEach(([key, value], index) => {
        if (!value) return;

        if (typeof value === "string") {
          out.push({
            id: `cred-${key}-${index}`,
            name: key,
            url: value,
            storagePath: "",
            gsUrl: "",
            expires: "",
            status: "",
            uploadedAt: "",
          });
          return;
        }

        if (typeof value === "object") {
          out.push({
            id: value.id || key || `cred-${index}`,
            name:
              value.name ||
              value.label ||
              value.title ||
              value.fileName ||
              value.filename ||
              value.originalName ||
              key ||
              "Credential",
            url:
              value.url ||
              value.downloadURL ||
              value.downloadUrl ||
              value.fileUrl ||
              value.fileURL ||
              value.storageUrl ||
              value.storageURL ||
              value.publicUrl ||
              value.publicURL ||
              value.href ||
              "",
            storagePath:
              value.storagePath ||
              value.fullPath ||
              value.path ||
              value.filePath ||
              value.storageRef ||
              "",
            gsUrl: value.gsUrl || value.gsURL || "",
            expires:
              value.expirationDate ||
              value.expires ||
              value.expiry ||
              value.expDate ||
              "",
            status: value.status || "",
            uploadedAt:
              value.uploadedAt ||
              value.createdAt ||
              value.timestamp ||
              value.dateUploaded ||
              "",
          });
        }
      });
    }
  });

  const deduped = [];
  const seen = new Set();

  out.forEach((item) => {
    const key = `${item.name}|${item.url}|${item.storagePath}|${item.gsUrl}|${item.expires}|${item.uploadedAt}`;
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(item);
  });

  return deduped;
}

function getProfileSummary(emp) {
  const missing = [];

  if (!hasValue(getEmployeeEmail(emp))) missing.push("Email");
  if (!hasValue(getPhoneText(emp))) missing.push("Phone");
  if (!hasValue(getAddressText(emp)) || getAddressText(emp) === "—") missing.push("Address");
  if (!hasValue(getEmergencyContactText(emp))) missing.push("Emergency Contact");
  if (!hasValue(getEmergencyPhoneText(emp))) missing.push("Emergency Phone");

  if (missing.length === 0) {
    return {
      label: "Complete",
      tone: "good",
      detail: "Profile complete",
    };
  }

  return {
    label: `Missing ${missing.length}`,
    tone: missing.length >= 3 ? "bad" : "warn",
    detail: missing.join(", "),
  };
}

function getAssetSummary(emp) {
  const hasPhoto = hasValue(getProfilePhotoUrl(emp));
  const hasSignature = hasValue(getSignatureUrl(emp));

  if (hasPhoto && hasSignature) {
    return {
      label: "Complete",
      tone: "good",
      detail: "Photo + signature on file",
    };
  }

  if (!hasPhoto && !hasSignature) {
    return {
      label: "Missing Both",
      tone: "bad",
      detail: "Photo, Signature",
    };
  }

  if (!hasPhoto) {
    return {
      label: "No Photo",
      tone: "warn",
      detail: "Profile photo missing",
    };
  }

  return {
    label: "No Signature",
    tone: "warn",
    detail: "Signature missing",
  };
}

function getCredentialSummary(emp) {
  const items = normalizeCredentialItems(emp);

  if (!items.length) {
    return {
      label: "Missing",
      tone: "bad",
      detail: "No credentials on file",
    };
  }

  const statuses = items.map((item) => {
    const explicit = String(item?.status || "").trim();
    if (explicit) return explicit;

    const raw = item?.expires;
    if (!raw) return "";

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";

    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    if (diffDays < 0) return "Expired";
    if (diffDays <= 60) return "Expiring Soon";
    return "Current";
  });

  const hasExpired = statuses.some((s) => String(s).toLowerCase().includes("expired"));
  const hasExpiring = statuses.some((s) => String(s).toLowerCase().includes("expiring"));

  if (hasExpired) {
    return {
      label: "Expired",
      tone: "bad",
      detail: "One or more credentials expired",
    };
  }

  if (hasExpiring) {
    return {
      label: "Expiring",
      tone: "warn",
      detail: "One or more credentials expiring soon",
    };
  }

  return {
    label: `${items.length} On File`,
    tone: "good",
    detail: `${items.length} credential${items.length === 1 ? "" : "s"} on file`,
  };
}

function getToneStyle(tone) {
  if (tone === "good") {
    return {
      background: "#dcfce7",
      color: "#166534",
      border: "1px solid #86efac",
    };
  }
  if (tone === "warn") {
    return {
      background: "#fff7ed",
      color: "#9a3412",
      border: "1px solid #fdba74",
    };
  }
  if (tone === "bad") {
    return {
      background: "#fee2e2",
      color: "#991b1b",
      border: "1px solid #fecaca",
    };
  }
  return {
    background: "#f1f5f9",
    color: "#334155",
    border: "1px solid #cbd5e1",
  };
}

function SummaryBadge({ summary }) {
  const style = getToneStyle(summary?.tone);

  return (
    <div
      title={summary?.detail || ""}
      style={{
        ...style,
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
        display: "inline-block",
      }}
    >
      {summary?.label || "—"}
    </div>
  );
}

export default function EmployeeList() {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState([]);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [sortBy, setSortBy] = useState("name");
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);

  const [bulkResetLoading, setBulkResetLoading] = useState(false);
  const [bootstrapLoading, setBootstrapLoading] = useState(false);

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageSubject, setMessageSubject] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [scheduleMessageDate, setScheduleMessageDate] = useState("");

  const [savingFieldId, setSavingFieldId] = useState("");

  const { role: myRole, loading: roleLoading } = useUserRole();
  const { can, loading: permsLoading } = useRolePermissions(myRole);

  const isSuper = isSuperRole(myRole);
  const canEditEmployees = isSuper || can("EmployeesEdit");

  const myEmail = normalizeEmail(auth.currentUser?.email || "");
  const canBootstrap = !isSuper && myEmail === BOOTSTRAP_EMAIL;

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmployees = async () => {
    const [usersSnap, appsSnap] = await Promise.all([
      getDocs(collection(db, "users")),
      getDocs(collection(db, "2026_Applications")),
    ]);

    const users = usersSnap.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));

    const apps = appsSnap.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));

    const appByUid = new Map();
    const appByEmail = new Map();

    apps.forEach((app) => {
      const uid = String(app.uid || app.authUid || "").trim();
      const email = normalizeEmail(app.email || app.emailKey || "");
      if (uid) appByUid.set(uid, app);
      if (email) appByEmail.set(email, app);
    });

    let allDays = {};
    try {
      allDays = await readAllDays();
    } catch (e) {
      console.warn("[EmployeeList] readAllDays failed (non-blocking):", e);
      allDays = {};
    }

    const hoursById = new Map();

    Object.values(allDays).forEach((dayObj) => {
      const day = dayObj || {};

      const assignmentsBag =
        day.__assignments && typeof day.__assignments === "object"
          ? day.__assignments
          : null;

      if (assignmentsBag) {
        Object.entries(assignmentsBag).forEach(([roleLabel, assignments]) => {
          if (!Array.isArray(assignments)) return;

          assignments.forEach((entry) => {
            const employeeId =
              typeof entry === "string"
                ? entry
                : entry?.employeeId || entry?.id || null;

            if (!employeeId) return;

            let hrs = getSlotHours(roleLabel);

            if (entry?.start && entry?.end) {
              const partialHours = timeStringToHours(entry.start, entry.end);
              if (partialHours > 0) hrs = partialHours;
            }

            hoursById.set(employeeId, (hoursById.get(employeeId) || 0) + hrs);
          });
        });

        return;
      }

      Object.entries(day).forEach(([roleLabel, ids]) => {
        if (!Array.isArray(ids)) return;

        const hrs = getSlotHours(roleLabel);
        ids.forEach((id) => {
          if (!id) return;
          hoursById.set(id, (hoursById.get(id) || 0) + hrs);
        });
      });
    });

    const merged = users.map((user) => {
      const userUid = String(user.uid || user.id || "").trim();
      const userEmail = normalizeEmail(
        user.email || user.emailKey || user.authEmail || user.userEmail || ""
      );

      const sourceApplicationId = String(
        user.sourceApplicationId || user.applicationId || ""
      ).trim();

      const appMatch =
        (sourceApplicationId
          ? apps.find((a) => a.id === sourceApplicationId)
          : null) ||
        appByUid.get(userUid) ||
        (userEmail ? appByEmail.get(userEmail) : null) ||
        null;

      const position =
        user.position ||
        user.title ||
        appMatch?.position ||
        user.providerLevel ||
        "";

      const basePosition =
        user.basePosition ||
        appMatch?.basePosition ||
        user.providerLevel ||
        appMatch?.providerLevel ||
        position ||
        "";

      const payTitle = user.payTitle || appMatch?.payTitle || "";

      return {
        ...appMatch,
        ...user,
        applicationData: appMatch || null,
        name:
          user.name ||
          user.displayName ||
          appMatch?.name ||
          appMatch?.displayName ||
          "",
        email:
          user.email ||
          user.emailKey ||
          user.authEmail ||
          user.userEmail ||
          appMatch?.email ||
          appMatch?.emailKey ||
          "",
        position,
        basePosition,
        payTitle,
        role: user.role || appMatch?.role || "Field User",
        applicationId: sourceApplicationId || appMatch?.id || "",
        active: typeof appMatch?.active === "boolean" ? appMatch.active : true,
        workedBefore: user.workedBefore ?? appMatch?.workedBefore ?? "",
        pastWorkYear: user.pastWorkYear ?? appMatch?.pastWorkYear ?? "",
        scheduledHours:
          hoursById.get(user.uid || user.id) || hoursById.get(user.id) || 0,
      };
    });

    const emailCounts = {};

users.forEach((u) => {
  const email = normalizeEmail(
    u.email || u.emailKey || u.authEmail || u.userEmail || ""
  );

  if (!email) return;

  emailCounts[email] = (emailCounts[email] || 0) + 1;
});

    const mergedWithDupes = merged.map((u) => {
      const normalizedEmail = normalizeEmail(u.email || u.applicationData?.email);
      return {
        ...u,
        normalizedEmail,
        isDuplicate: !!normalizedEmail && emailCounts[normalizedEmail] > 1,
        duplicateCount: normalizedEmail ? emailCounts[normalizedEmail] || 0 : 0,
      };
    });

   const cleaned = [];
const seen = new Set();

mergedWithDupes.forEach((emp) => {
  const key = normalizeEmail(emp.email);

  if (!key) return;

  if (seen.has(key)) return;

  seen.add(key);
  cleaned.push(emp);
});

setEmployees(cleaned.filter((e) => e.active !== false));
  };

  const filteredEmployees = useMemo(() => {
    if (!showDuplicatesOnly) return employees;
    return employees.filter((emp) => emp.isDuplicate);
  }, [employees, showDuplicatesOnly]);

  const sortedEmployees = useMemo(() => {
    const arr = [...filteredEmployees];

    if (sortBy === "name") {
      arr.sort((a, b) =>
        String(getEmployeeName(a)).localeCompare(String(getEmployeeName(b)))
      );
    } else if (sortBy === "hours") {
      arr.sort(
        (a, b) => Number(b.scheduledHours || 0) - Number(a.scheduledHours || 0)
      );
    } else if (sortBy === "position") {
      arr.sort((a, b) =>
        String(getEmployeePosition(a)).localeCompare(String(getEmployeePosition(b)))
      );
    } else if (sortBy === "email") {
      arr.sort((a, b) =>
        String(getEmployeeEmail(a)).localeCompare(String(getEmployeeEmail(b)))
      );
    }

    return arr;
  }, [filteredEmployees, sortBy]);

  const duplicateCount = useMemo(() => {
    return employees.filter((emp) => emp.isDuplicate).length;
  }, [employees]);

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const visibleIds = sortedEmployees.map((e) => e.id);
    const allVisibleSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));

    if (allVisibleSelected) {
      setSelected((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

 const toggleActiveStatus = async (emp) => {
  const applicationId = String(emp?.applicationId || "").trim();

  if (!applicationId) {
    alert("No linked application record found for this user.");
    return;
  }

  const appRef = doc(db, "2026_Applications", applicationId);
  const appSnap = await getDoc(appRef);

  if (!appSnap.exists()) {
    alert("Linked application record not found.");
    return;
  }

  const currentStatus = !!appSnap.data()?.active;
  const newStatus = !currentStatus;

  // Update application record
  await updateDoc(appRef, {
    active: newStatus,
  });

  // Update users record
  await updateDoc(doc(db, "users", emp.id), {
    active: newStatus,
    "applicationData.active": newStatus,
  });

  await fetchEmployees();
}; 

  const bootstrapMe = async () => {
    if (!auth.currentUser) return alert("You must be signed in.");
    if (!canBootstrap) return alert("Bootstrap is not available for this account.");

    try {
      setBootstrapLoading(true);

      const res = await callBootstrapSuperUser({});
      console.log("bootstrapSuperUser result:", res);

      await auth.currentUser.getIdToken(true);

      alert("✅ You are now Super User. Refreshing…");
      await fetchEmployees();
    } catch (e) {
      console.error("bootstrapSuperUser failed:", e);
      alert(prettyErr(e, "Bootstrap failed."));
    } finally {
      setBootstrapLoading(false);
    }
  };

  const handleRoleChange = async (emp, newRole) => {
    if (!isSuper && !can("EmployeesEdit")) {
      return alert("You do not have permission to change roles.");
    }

    const targetUid = String(emp?.uid || emp?.id || "").trim();
    if (!targetUid) {
      return alert("No UID found yet for this person.");
    }

    try {
      const res = await callSetUserRole({ uid: targetUid, role: newRole });
      console.log("callSetUserRole result:", res);

      await updateDoc(doc(db, "users", emp.id), { role: newRole });

      const applicationId = String(emp?.applicationId || "").trim();
      if (applicationId) {
        const appRef = doc(db, "2026_Applications", applicationId);
        const appSnap = await getDoc(appRef);
        if (appSnap.exists()) {
          await updateDoc(appRef, { role: newRole });
        }
      }

      if (auth.currentUser && auth.currentUser.uid === targetUid) {
        await auth.currentUser.getIdToken(true);
      }

      await fetchEmployees();
    } catch (e) {
      console.error("setUserRole failed:", e);
      alert(prettyErr(e, "Failed to update role."));
    }
  };

  const handleCompChange = async (emp, patch) => {
    if (!isSuper && !can("EmployeesEdit")) {
      return alert("You do not have permission to edit employee settings.");
    }

    const nextPosition =
      patch.position !== undefined ? patch.position : getEmployeePosition(emp);

    const nextBasePosition =
      patch.basePosition !== undefined
        ? patch.basePosition
        : getEmployeeBasePosition(emp);

    const nextPayTitle =
      patch.payTitle !== undefined
        ? patch.payTitle
        : getEmployeeRateLabel(emp);

    const finalPatch = {
      position: nextPosition,
      basePosition: nextBasePosition,
      providerLevel: nextBasePosition,
      payTitle: nextPayTitle,
    };

    const fieldKey = `${emp.id}:${Object.keys(patch).join(",")}`;
    setSavingFieldId(fieldKey);

    try {
      await updateDoc(doc(db, "users", emp.id), finalPatch);

      const applicationId = String(emp?.applicationId || "").trim();
      if (applicationId) {
        const appRef = doc(db, "2026_Applications", applicationId);
        const appSnap = await getDoc(appRef);
        if (appSnap.exists()) {
          await updateDoc(appRef, finalPatch);
        }
      }

      setEmployees((prev) =>
        prev.map((row) =>
          row.id === emp.id
            ? {
                ...row,
                ...finalPatch,
              }
            : row
        )
      );

      if (viewingEmployee?.id === emp.id) {
        setViewingEmployee((prev) =>
          prev
            ? {
                ...prev,
                ...finalPatch,
              }
            : prev
        );
      }
    } catch (e) {
      console.error("handleCompChange failed:", e);
      alert(prettyErr(e, "Failed to update employee settings."));
    } finally {
      setSavingFieldId("");
    }
  };

  const handleDisplayPositionChange = async (emp, newPosition) => {
    const currentBase = getEmployeeBasePosition(emp);
    const currentRateLabel = getEmployeeRateLabel(emp);

    const nextBasePosition = getDefaultBasePosition(newPosition, currentBase);
    const nextPayTitle = getDefaultRateLabel(newPosition, currentRateLabel);

    await handleCompChange(emp, {
      position: newPosition,
      basePosition: nextBasePosition,
      payTitle: nextPayTitle,
    });
  };

  const sendBulkResetLinks = async () => {
  if (!isSuper) return alert("Only Super User can generate reset emails.");
  if (selected.length === 0) return alert("Select at least one employee.");

  const selectedEmployees = employees.filter((e) => selected.includes(e.id));
  if (selectedEmployees.length === 0) {
    return alert("No matching employees selected.");
  }

  const ok = window.confirm(
    `Send password reset emails to ${selectedEmployees.length} selected employee(s)?`
  );
  if (!ok) return;

  try {
    setBulkResetLoading(true);

    const recipients = selectedEmployees
      .map((e) => ({
        email: normalizeEmail(getEmployeeEmail(e) || ""),
        uid: e.uid || e.id || null,
      }))
      .filter((r) => r.email);

    if (recipients.length === 0) {
      return alert("No valid emails found in selected employees.");
    }

    const res = await callNotifyApplicantsBulk(recipients);
    const data = res?.data ?? res;

    console.log("[sendBulkResetLinks] result:", data);

    const successCount = Number(data?.successCount || 0);
    const failures = Array.isArray(data?.failures) ? data.failures : [];
    const failureCount = failures.length;

    alert(
      `Reset link emailed to ${successCount} recipient${
        successCount === 1 ? "" : "s"
      }` + (failureCount ? ` — ${failureCount} failed` : "")
    );

    setSelected([]);
  } catch (e) {
    console.error("notifyApplicantsBulk failed:", e);
    alert(prettyErr(e, "Bulk reset failed."));
  } finally {
    setBulkResetLoading(false);
  }
};

const toIsoDate = (date = new Date()) => {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const getScheduledEmployeeIdsForDate = async (isoDate) => {
  const allDays = await readAllDays();
  const day = allDays?.[isoDate];

  if (!day) return [];

  const ids = new Set();

  const assignmentsBag =
    day.__assignments && typeof day.__assignments === "object"
      ? day.__assignments
      : null;

  if (assignmentsBag) {
    Object.values(assignmentsBag).forEach((assignments) => {
      if (!Array.isArray(assignments)) return;

      assignments.forEach((entry) => {
        const employeeId =
          typeof entry === "string"
            ? entry
            : entry?.employeeId || entry?.id || null;

        if (employeeId) ids.add(employeeId);
      });
    });

    return Array.from(ids);
  }

  Object.values(day).forEach((value) => {
    if (!Array.isArray(value)) return;

    value.forEach((entry) => {
      const employeeId =
        typeof entry === "string"
          ? entry
          : entry?.employeeId || entry?.id || null;

      if (employeeId) ids.add(employeeId);
    });
  });

  return Array.from(ids);
};

const openScheduleMessageModal = async (isoDate) => {
  if (!isSuper) return alert("Only Super User can send messages.");

  try {
    const scheduledIds = await getScheduledEmployeeIdsForDate(isoDate);

    const matchedEmployees = employees.filter((emp) =>
      scheduledIds.includes(emp.uid || emp.id)
    );

    const matchedIds = matchedEmployees.map((emp) => emp.id);

    if (matchedIds.length === 0) {
      return alert("No scheduled employees found for that date.");
    }

    setSelected(matchedIds);
    setMessageSubject("");
    setMessageBody("");
    setShowMessageModal(true);
  } catch (e) {
    console.error(e);
    alert("Could not load scheduled employees for that date.");
  }
};

  const openMessageModal = () => {
    if (!isSuper) return alert("Only Super User can send messages.");
    if (selected.length === 0) return alert("Select at least one employee.");
    setMessageSubject("");
    setMessageBody("");
    setShowMessageModal(true);
  };

  const sendCustomMessage = async () => {
    if (!isSuper) return alert("Only Super User can send messages.");
    if (selected.length === 0) return alert("Select at least one employee.");
    if (!messageSubject.trim() || !messageBody.trim()) {
      return alert("Subject and message body are required.");
    }

    const selectedEmployees = employees.filter((e) => selected.includes(e.id));

    const recipients = selectedEmployees
      .map((e) => ({
        email: normalizeEmail(getEmployeeEmail(e) || ""),
        uid: e.uid || e.id || null,
        name: getEmployeeName(e),
      }))
      .filter((r) => r.email);

    if (recipients.length === 0) return alert("No valid emails found.");

    try {
      setSendingMessage(true);

      const payload = {
        recipients,
        subject: messageSubject.trim(),
        message: messageBody.trim(),
        body: messageBody.trim(),
      };

      const res = await callSendCustomEmailBulk(payload);
      const data = res?.data ?? res;

      console.log("[sendCustomMessage] result:", data);

      const successCount = Number(data?.successCount ?? data?.sent ?? 0);
      const failures = Array.isArray(data?.failures) ? data.failures : [];
      const failureCount = failures.length;

      alert(
        `✅ Message sent to ${successCount} recipient${
          successCount === 1 ? "" : "s"
        }` + (failureCount ? ` — ${failureCount} failed` : "")
      );

      setShowMessageModal(false);
      setMessageSubject("");
      setMessageBody("");
      setSelected([]);
    } catch (e) {
      console.error(e);
      alert(e?.message || "Failed to send message.");
    } finally {
      setSendingMessage(false);
    }
  };

  const revealPin = async (emp) => {
    try {
      const applicationId = String(emp?.applicationId || "").trim();

      if (!applicationId) return alert("No linked application record found.");

      const snap = await getDoc(doc(db, "2026_Applications", applicationId));
      if (!snap.exists()) return alert("Employee record not found.");

      const data = snap.data();
      const pin = String(data?.pinLast4 || data?.ssn || "").trim();
      if (!pin) return alert("No PIN on file for this person.");

      alert(`${getEmployeeName(emp)}'s PIN (Last 4 of SSN) is: ${pin}`);
    } catch (e) {
      console.error(e);
      alert("Could not fetch PIN.");
    }
  };

  if (roleLoading || permsLoading) {
    return <div style={{ padding: 20 }}>Loading…</div>;
  }

  if (!isSuper && !can("EmployeesRead")) {
    return (
      <div style={{ padding: 20 }}>
        <AdminPageHeader title="Employee List" />
        <p>You do not have access to this page.</p>
      </div>
    );
  }

  const visibleIds = sortedEmployees.map((e) => e.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));

  return (
    <div className="page-container employee-list-page">
      <AdminPageHeader title="Active Employees" />

      <div style={{ maxWidth: 520 }}>
        <label style={{ fontWeight: 700 }}>Sort by:</label>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{ width: "100%" }}
        >
          <option value="name">A–Z</option>
          <option value="hours">Hours Scheduled</option>
          <option value="position">Position</option>
          <option value="email">Email</option>
        </select>
      </div>

      <div
        style={{
          display: "flex",
          gap: 10,
          marginTop: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <button
          className="btn btn-primary"
          onClick={() => setShowDuplicatesOnly((prev) => !prev)}
        >
          {showDuplicatesOnly ? "Show All Employees" : "Show Duplicates Only"}
        </button>

        <div
          style={{
            fontWeight: 700,
            color: duplicateCount > 0 ? "#9a3412" : "#166534",
          }}
        >
          Duplicate rows: {duplicateCount}
        </div>

        <button
  className="btn btn-secondary"
  onClick={fixDuplicateEmployees}
>
  Fix Duplicates
</button>

        <button
          className="btn btn-primary"
          onClick={sendBulkResetLinks}
          disabled={bulkResetLoading || selected.length === 0 || !isSuper}
        >
          {bulkResetLoading ? "Sending..." : "Send Reset Links"}
        </button>

        <button
          className="btn btn-secondary"
          onClick={openMessageModal}
          disabled={selected.length === 0 || !isSuper}
        >
          Send Message
        </button>

        <button
  className="btn btn-primary"
  onClick={() => openScheduleMessageModal(toIsoDate())}
  disabled={!isSuper}
>
  Message Today&apos;s Schedule
</button>

<div style={{ display: "flex", gap: 6, alignItems: "center" }}>
  <input
    type="date"
    value={scheduleMessageDate}
    onChange={(e) => setScheduleMessageDate(e.target.value)}
  />

  <button
    className="btn btn-secondary"
    onClick={() => openScheduleMessageModal(scheduleMessageDate)}
    disabled={!isSuper || !scheduleMessageDate}
  >
    Message Selected Day
  </button>
</div>

        <button className="btn btn-primary" disabled>
          Create Time Card
        </button>

        {canBootstrap && (
          <button
            className="btn btn-primary"
            onClick={bootstrapMe}
            disabled={bootstrapLoading}
          >
            {bootstrapLoading ? "Bootstrapping..." : "Bootstrap Super User"}
          </button>
        )}
      </div>

      <div className="table-wrapper">
        <table className="submissions-table employee-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>Name</th>
              <th>Position</th>
              <th>Last Login</th>
              <th>Scheduled Hours</th>
              <th>Profile</th>
              <th>Credentials</th>
              <th>Assets</th>
              <th>Status</th>
              <th>Role</th>
              <th>View</th>
              <th>PIN</th>
            </tr>
          </thead>

          <tbody>
            {sortedEmployees.map((emp) => {
              const isSavingAnyField = savingFieldId.startsWith(`${emp.id}:`);
              const profileSummary = getProfileSummary(emp);
              const credentialSummary = getCredentialSummary(emp);
              const assetSummary = getAssetSummary(emp);

              return (
                <tr key={emp.id} className={getEmployeeRowClass(emp)}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.includes(emp.id)}
                      onChange={() => toggleSelect(emp.id)}
                    />
                  </td>

                  <td>
                    <div style={{ fontWeight: 700 }}>{getEmployeeName(emp)}</div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                      {getEmployeeEmail(emp) || "No email"}
                      {emp.isDuplicate ? " • Duplicate" : ""}
                    </div>
                  </td>

                  <td>
                    <select
                      value={getEmployeePosition(emp) || ""}
                      onChange={(e) => handleDisplayPositionChange(emp, e.target.value)}
                      disabled={(!isSuper && !can("EmployeesEdit")) || isSavingAnyField}
                    >
                      {DISPLAY_POSITION_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>
                  {emp.lastLoginAt?.toDate
                    ? emp.lastLoginAt.toDate().toLocaleString()
                    : "—"}
                </td>

                  <td>{Number(emp.scheduledHours || 0)}</td>

                  <td>
                    <SummaryBadge summary={profileSummary} />
                  </td>

                  <td>
                    <SummaryBadge summary={credentialSummary} />
                  </td>

                  <td>
                    <SummaryBadge summary={assetSummary} />
                  </td>

                  <td>
                    {canEditEmployees ? (
  <button
    className="btn btn-primary"
    onClick={() => toggleActiveStatus(emp)}
    disabled={!emp.applicationId}
  >
    {emp.active === false ? "Inactive" : "Active"}
  </button>
) : (
  <span className="btn btn-primary" style={{ cursor: "default" }}>
    {emp.active === false ? "Inactive" : "Active"}
  </span>
)}
                  </td>

                  <td>
                    <select
                      value={emp.role || "Field User"}
                      onChange={(e) => handleRoleChange(emp, e.target.value)}
                      disabled={!isSuper && !can("EmployeesEdit")}
                    >
                      <option>Field User</option>
                      <option>Supervisor</option>
                      <option>Admin</option>
                      <option>Super User</option>
                    </select>
                  </td>

                  <td>
                    <button
                      className="btn btn-primary"
                      onClick={() => setViewingEmployee(emp)}
                    >
                      View / Edit
                    </button>
                  </td>

                  <td>
                    {canEditEmployees ? (
  <button
    className="btn btn-secondary"
    onClick={() => revealPin(emp)}
  >
    Reveal PIN
  </button>
) : (
  <span style={{ color: "#64748b", fontWeight: 700 }}>Hidden</span>
)}
                  </td>
                </tr>
              );
            })}

            {sortedEmployees.length === 0 && (
              <tr>
                <td colSpan={11} style={{ padding: 16 }}>
                  {showDuplicatesOnly
                    ? "No duplicate employees found."
                    : "No active employees found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ProfileModal
        open={!!viewingEmployee}
        onClose={() => {
          setViewingEmployee(null);
          fetchEmployees();
        }}
        userId={viewingEmployee?.id}
        canEdit={isSuper || can("EmployeesEdit")}
      />

      {showMessageModal && (
        <div className="application-modal-overlay">
          <div className="application-modal">
            <h2>Send Message</h2>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ fontWeight: 700 }}>
                Sending to {selected.length} selected employee
                {selected.length === 1 ? "" : "s"}
              </div>

              <input
                type="text"
                placeholder="Subject"
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
              />

              <textarea
                rows={8}
                placeholder="Message body..."
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
                style={{ resize: "vertical" }}
              />
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button
                onClick={() => {
                  if (sendingMessage) return;
                  setShowMessageModal(false);
                }}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                disabled={!messageSubject.trim() || !messageBody.trim() || sendingMessage}
                onClick={sendCustomMessage}
              >
                {sendingMessage ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}